import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, query, where, getDocs, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './AddStudent.css';


function AddStudent({ onStudentAdded, onCancel, enrollmentData }) {
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [generatedAccountNumber, setGeneratedAccountNumber] = useState('');
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [adminFeeStructure, setAdminFeeStructure] = useState(null);
const [adminFeeBreakdown, setAdminFeeBreakdown] = useState(null);
  const [formData, setFormData] = useState({
    studentName: '',
    dateOfBirth: '',
    class: '',
    startDate: new Date().toISOString().split('T')[0],
    primaryContactName: '',
    primaryContactId: '',
    primaryContactCellPhone: '',
    primaryContactWorkPhone: '',
    primaryContactHomePhone: '',
    primaryContactEmail: '',
    primaryContactRelationship: '',
    secondaryContactName: '',
    secondaryContactCellPhone: '',
    secondaryContactWorkPhone: '',
    secondaryContactHomePhone: '',
    secondaryContactEmail: '',
    secondaryContactRelationship: '',
    adminFee: '',
    paymentMethod: 'term',
    tShirtSize: ''  
  });

  useEffect(() => {
    loadSchools();
    loadAdminFeeStructure();
  }, []);

  useEffect(() => {
    if (formData.adminFee && adminFeeStructure) {
      const breakdown = calculateAdminFeeBreakdown(formData.adminFee);
      setAdminFeeBreakdown(breakdown);
    }
  }, [formData.adminFee, adminFeeStructure]);

  useEffect(() => {
    if (selectedSchool && formData.studentName.trim()) {
      generateAccountNumber();
    } else {
      setGeneratedAccountNumber('');
    }
  }, [selectedSchool, formData.studentName]);

  useEffect(() => {
    if (selectedSchool) {
      setFormData(prev => ({
        ...prev,
        adminFee: selectedSchool.adminFee || 160
      }));
    }
  }, [selectedSchool]);
// Pre-fill form if enrollment data is provided
useEffect(() => {
  if (enrollmentData && schools.length > 0) {
    // Find matching school
    const matchedSchool = schools.find(s => 
      enrollmentData.schoolRef ? s.id === enrollmentData.schoolRef.id : 
      s.schoolName.toLowerCase() === enrollmentData.schoolName.toLowerCase()
    );
    
    if (matchedSchool) {
      setSelectedSchool(matchedSchool);
    }
    
    // Pre-fill form data
    setFormData(prev => ({
      ...prev,
      studentName: enrollmentData.studentName || '',
      class: enrollmentData.class || '',
      primaryContactName: enrollmentData.paymentResponsible || '',
      primaryContactEmail: enrollmentData.email || '',
      primaryContactCellPhone: enrollmentData.whatsapp || '',
      primaryContactHomePhone: enrollmentData.telephone || ''
    }));
    
    // Store enrollment ID for deletion after successful add
    if (enrollmentData.enrollmentId) {
      setEnrollmentId(enrollmentData.enrollmentId);
    }
  }
}, [enrollmentData, schools]);


  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const schoolsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSchools(schoolsList);
      
      if (schoolsList.length === 0) {
        alert('You need to add at least one school before adding students. Please add a school first.');
      }
    } catch (error) {
      console.error('Error loading schools:', error);
      alert('Error loading schools');
    } finally {
      setLoadingSchools(false);
    }
  };
  const loadAdminFeeStructure = async () => {
    try {
      const adminFeeDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      
      if (adminFeeDoc.exists()) {
        setAdminFeeStructure(adminFeeDoc.data());
      } else {
        console.log('No admin fee structure found - using defaults');
        // Set default structure if none exists
        setAdminFeeStructure({
          defaultAdminFee: 160,
          standardBreakdown: {
            brandBuilding: 20,
            franchiseeKeeps: 20,
            headOffice: 120,
            tshirtIncluded: true
          },
          waivers: {
            noFee: {
              brandBuilding: 0,
              franchiseeKeeps: 0,
              headOffice: 0,
              tshirtIncluded: false
            },
            partialFee: {
              amount: 40,
              brandBuilding: 20,
              franchiseeKeeps: 20,
              headOffice: 0,
              tshirtIncluded: false
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading admin fee structure:', error);
    }
  };
  const calculateAdminFeeBreakdown = (adminFeeAmount) => {
  if (!adminFeeStructure) {
    return null;
  }

  const amount = parseFloat(adminFeeAmount);

  // Case 1: No admin fee (R0)
  if (amount === 0) {
    return {
      total: 0,
      bbContribution: adminFeeStructure.waiverRules?.zero?.bb || 0,
      franchiseeKeeps: adminFeeStructure.waiverRules?.zero?.franchisee || 0,
      headOfficeGets: adminFeeStructure.waiverRules?.zero?.ho || 0,
      tshirtIncluded: adminFeeStructure.waiverRules?.zero?.tshirt || false
    };
  }

  // Case 2: Partial fee (R40)
  if (amount === 40) {
    return {
      total: amount,
      bbContribution: adminFeeStructure.waiverRules?.partial40?.bb || 20,
      franchiseeKeeps: adminFeeStructure.waiverRules?.partial40?.franchisee || 20,
      headOfficeGets: adminFeeStructure.waiverRules?.partial40?.ho || 0,
      tshirtIncluded: adminFeeStructure.waiverRules?.partial40?.tshirt || false
    };
  }

  // Case 3: Standard fee (R160 or R200 - defaultAdminFee)
  if (amount === adminFeeStructure.defaultAdminFee || amount === 160 || amount === 200) {
    return {
      total: amount,
      bbContribution: adminFeeStructure.brandBuildingFund || 20,
      franchiseeKeeps: adminFeeStructure.franchiseeKeeps || 20,
      headOfficeGets: adminFeeStructure.headOfficeGets || 160,
      tshirtIncluded: adminFeeStructure.includesTshirt !== undefined ? adminFeeStructure.includesTshirt : true
    };
  }

  // Case 4: Custom amount
  // R20 to Brand Building, remainder to Franchisee
  return {
    total: amount,
    bbContribution: 20,
    franchiseeKeeps: amount - 20,
    headOfficeGets: 0,
    tshirtIncluded: false
  };
};
  const createBrandBuildingContribution = async (studentRef, studentData, breakdown) => {
    // Only create if there's a BB contribution amount
    if (!breakdown || breakdown.bbContribution <= 0) {
      return null;
    }

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      // Get franchisee name
      const franchiseeDoc = await getDoc(franchiseeRef);
      const franchiseeName = franchiseeDoc.exists() ? franchiseeDoc.data().name : 'Unknown';

      const contributionData = {
        franchiseeRef: franchiseeRef,
        franchiseeName: franchiseeName,
        
        studentRef: studentRef,
        studentName: studentData.studentName,
        schoolName: studentData.schoolName,
        
        amount: breakdown.bbContribution,
        adminFeePaidDate: null,  // Set when admin fee is actually paid
        
        // Month-end tracking (set during month-end processing)
        monthEndPeriod: null,
        monthEndDate: null,
        
        paid: false,
        paidDate: null,
        
        createdAt: new Date()
      };

      const bbRef = await addDoc(collection(db, 'brandBuildingContributions'), contributionData);
      return bbRef.id;
      
    } catch (error) {
      console.error('Error creating brand building contribution:', error);
      return null;
    }
  };

  const generateAccountNumber = () => {
    if (!selectedSchool || !formData.studentName.trim()) return;
    
    const schoolCode = selectedSchool.schoolCode;
    const fullName = formData.studentName.trim();
    
    const nameParts = fullName.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 0) return;
    
    const firstName = nameParts[0];
    const restOfName = nameParts.slice(1).join('');
    const surnamePrefix = restOfName.slice(0, 3);
    
    const accountNumber = restOfName 
      ? `${schoolCode}-${firstName}-${surnamePrefix}`
      : `${schoolCode}-${firstName}`;
    
    setGeneratedAccountNumber(accountNumber);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSchoolChange = (e) => {
    const schoolId = e.target.value;
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchool(school);
  };

  const initializeTermPayments = () => {
    // Initialize all term payments to 0
    return {
      admin: 0,
      term1: 0,
      term2: 0,
      term3: 0,
      term4: 0
    };
  };

  const initializeInvoices = () => {
    // Initialize invoice tracking (all null until invoiced)
    return {
      term1: null,
      term2: null,
      term3: null,
      term4: null
    };
  };

  // Calculate total expected based on start date and term structure
  const calculateTotalExpected = async (startDate, schoolRef) => {
    try {
      // Get school data
      const schoolDoc = await getDoc(schoolRef);
      if (!schoolDoc.exists()) {
        return parseFloat(formData.adminFee || 0);
      }

      const schoolData = schoolDoc.data();
      
      // Get term structure
      if (!schoolData.termStructureRef) {
        return parseFloat(formData.adminFee || 0);
      }

      const termStructureDoc = await getDoc(schoolData.termStructureRef);
      if (!termStructureDoc.exists()) {
        return parseFloat(formData.adminFee || 0);
      }

      const termStructure = termStructureDoc.data();
      if (!termStructure.termDates) {
        return parseFloat(formData.adminFee || 0);
      }

      // Calculate which terms student should be charged for
      const studentStartDate = new Date(startDate);
      studentStartDate.setHours(0, 0, 0, 0);
      
      const currentYear = new Date().getFullYear();
      const termFee = formData.paymentMethod === 'term' 
        ? selectedSchool.schoolFee.termFee 
        : selectedSchool.schoolFee.instalmentFee;
      
      let totalTermFees = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check each term (up to 4)
      for (let termNum = 1; termNum <= 4; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];
        if (!termData || !termData.start || !termData.end) continue;

        const [startMonth, startDay] = termData.start.split('-').map(Number);
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        
        const termStart = new Date(currentYear, startMonth - 1, startDay);
        termStart.setHours(0, 0, 0, 0);
        
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);

        // Student should be charged if:
        // 1. They started before the term ended
        // 2. Today's date is past the term start (term has begun)
        if (studentStartDate <= termEnd && today >= termStart) {
          totalTermFees += termFee;
        }
      }

      const adminFee = parseFloat(formData.adminFee || 0);
      return adminFee + totalTermFees;
      
    } catch (error) {
      console.error('Error calculating total expected:', error);
      return parseFloat(formData.adminFee || 0);
    }
  };

  const handleSubmit = async (e, continueAdding = false) => {
  e.preventDefault();
  setLoading(true);
    
    if (!selectedSchool) {
      alert('Please select a school');
      return;
    }
    
    if (!formData.studentName.trim()) {
      alert('Please enter student name');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const schoolRef = doc(db, 'schools', selectedSchool.id);

      const termPayments = initializeTermPayments();
      const invoices = initializeInvoices();

      // Calculate total expected based on start date
      const totalExpected = await calculateTotalExpected(
        formData.startDate,
        schoolRef
      );

      const studentData = {
        studentName: formData.studentName.trim(),
        accountNumber: generatedAccountNumber,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : null,
        tShirtSize: formData.tShirtSize || null,
        class: formData.class,
        startDate: new Date(formData.startDate),
        isActive: true,
        schoolRef: schoolRef,
        schoolName: selectedSchool.schoolName,
        franchiseeRef: franchiseeRef,
        
        adminFee: parseFloat(formData.adminFee || 0),
        adminFeeYear: new Date().getFullYear(),
        adminFeePaid: 0,  // NEW: Track amount paid
        
        // NEW: Admin Fee Breakdown
        adminFeeBreakdown: adminFeeBreakdown || {
          total: parseFloat(formData.adminFee || 0),
          bbContribution: 0,
          franchiseeKeeps: 0,
          headOfficeGets: 0,
          tshirtIncluded: false
        },
        
        // NEW: T-shirt Tracking
        tshirtOrdered: false,
        tshirtOrderId: null,
        tshirtOrderDate: null,
        
        // NEW: Brand Building
        bbContributionDue: adminFeeBreakdown?.bbContribution || 0,
        bbContributionPaid: false,
        bbContributionDate: null,
        
        primaryContact: {
          name: formData.primaryContactName,
          idNumber: formData.primaryContactId,
          cellPhone: formData.primaryContactCellPhone,
          workPhone: formData.primaryContactWorkPhone,
          homePhone: formData.primaryContactHomePhone,
          email: formData.primaryContactEmail,
          relationship: formData.primaryContactRelationship
        },
        
        secondaryContact: {
          name: formData.secondaryContactName,
          cellPhone: formData.secondaryContactCellPhone,
          workPhone: formData.secondaryContactWorkPhone,
          homePhone: formData.secondaryContactHomePhone,
          email: formData.secondaryContactEmail,
          relationship: formData.secondaryContactRelationship
        },
        
        feeStructure: {
          standardFee: formData.paymentMethod === 'term' 
            ? selectedSchool.schoolFee.termFee 
            : selectedSchool.schoolFee.instalmentFee,
          lessonsPerTerm: selectedSchool.lessonsPerTerm || 10,
          paymentMethod: formData.paymentMethod
        },
        
        // NEW: Simple payment tracking
        termPayments: termPayments,
        creditBalance: 0,
        
        // NEW: Financial summary
        totalExpected: totalExpected,
        totalPaid: 0,
        balance: -totalExpected,  // Negative = owing
        
        // Invoice tracking (optional)
        invoices: invoices,
        
        // Payment history
        paymentHistory: [],
        
        // Status
        paymentStatus: totalExpected > 0 ? 'outstanding' : 'paid',
        lastPaymentDate: null,
        createdDate: new Date(),
        updatedAt: new Date()
      };

      const studentRef = await addDoc(collection(db, 'students'), studentData);

// Create Brand Building Contribution if applicable
if (adminFeeBreakdown && adminFeeBreakdown.bbContribution > 0) {
  await createBrandBuildingContribution(studentRef, studentData, adminFeeBreakdown);
}
      if (enrollmentId) {
  try {
    await deleteDoc(doc(db, 'pendingEnrollments', enrollmentId));
  } catch (error) {
    console.error('Error deleting pending enrollment:', error);
  }
}
      const schoolDoc = await getDoc(schoolRef);
      const currentCount = schoolDoc.data().numberOfStudents || 0;
      await updateDoc(schoolRef, {
        numberOfStudents: currentCount + 1
      });
      
      if (continueAdding) {
  // Reset form but keep school selected
  const keepSchool = selectedSchool;
  setFormData({
    studentName: '',
    dateOfBirth: '',
    class: '',
    startDate: new Date().toISOString().split('T')[0],
    primaryContactName: '',
    primaryContactId: '',
    primaryContactCellPhone: '',
    primaryContactWorkPhone: '',
    primaryContactHomePhone: '',
    primaryContactEmail: '',
    primaryContactRelationship: '',
    secondaryContactName: '',
    secondaryContactCellPhone: '',
    secondaryContactWorkPhone: '',
    secondaryContactHomePhone: '',
    secondaryContactEmail: '',
    secondaryContactRelationship: '',
    adminFee: keepSchool.adminFee || 160,
    paymentMethod: 'term',
    tShirtSize: ''
  });
  setSelectedSchool(keepSchool);
  setGeneratedAccountNumber('');
  alert('Student added successfully! Add another student.');
} else {
  alert('Student added successfully!');
  onStudentAdded();
}
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Error adding student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSchools) {
    return (
      <div className="add-student-container">
        <div className="add-student-form">
          <div className="loading">Loading schools...</div>
        </div>
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="add-student-container">
        <div className="add-student-form">
          <div className="no-schools">
            <h2>No Schools Found</h2>
            <p>You need to add at least one school before adding students.</p>
            <button onClick={onCancel} className="btn-cancel">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-student-container">
      <div className="add-student-form">
        <div className="form-header">
          <h1>Add New Student</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>School Information</h2>
            
            <div className="form-group">
              <label>Select School *</label>
              <select
                value={selectedSchool?.id || ''}
                onChange={handleSchoolChange}
                required
              >
                <option value="">-- Choose a school --</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.schoolName} ({school.schoolCode})
                  </option>
                ))}
              </select>
            </div>

            {selectedSchool && (
              <div className="school-fee-display" style={{
                background: '#e7f3ff',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '10px'
              }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#4169E1' }}>Fee Structure:</h3>
                <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                  <div>
                    <strong>Term Fee:</strong> R{selectedSchool.schoolFee.termFee}
                  </div>
                  <div>
                    <strong>Instalment Fee:</strong> R{selectedSchool.schoolFee.instalmentFee}
                  </div>
                  <div>
                    <strong>Terms:</strong> {selectedSchool.schoolFee.termType}
                  </div>
                  <div>
                    <strong>Instalments/Term:</strong> {selectedSchool.instalmentType}
                  </div>
                  <div>
                    <strong>Lessons/Term:</strong> {selectedSchool.lessonsPerTerm || 10}
                  </div>
                </div>
                <div style={{ marginTop: '10px', fontSize: '14px' }}>
                  <strong>Admin Fee:</strong> R{selectedSchool.adminFee || 160}
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Student Information</h2>
            
            <div className="form-group">
              <label>Student Full Name *</label>
              <input
                type="text"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                required
                placeholder="e.g., John Smith"
              />
            </div>

            {generatedAccountNumber && (
              <div className="account-number-display" style={{
                background: '#e8f5e9',
                padding: '12px',
                borderRadius: '5px',
                marginTop: '10px'
              }}>
                <strong style={{ color: '#2e7d32' }}>Generated Account Number:</strong>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1b5e20', marginTop: '5px' }}>
                  {generatedAccountNumber}
                </div>
                <small style={{ color: '#2e7d32' }}>This will be used for payment references</small>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label>Class/Grade</label>
                <input
                  type="text"
                  name="class"
                  value={formData.class}
                  onChange={handleChange}
                  placeholder="e.g., Grade 2, Dino Class"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Primary Contact</h2>
            
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="primaryContactName"
                value={formData.primaryContactName}
                onChange={handleChange}
                required
                placeholder="e.g., Sarah Smith"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ID Number</label>
                <input
                  type="text"
                  name="primaryContactId"
                  value={formData.primaryContactId}
                  onChange={handleChange}
                  placeholder="9001015800080"
                />
              </div>
              
              <div className="form-group">
                <label>Relationship</label>
                <input
                  type="text"
                  name="primaryContactRelationship"
                  value={formData.primaryContactRelationship}
                  onChange={handleChange}
                  placeholder="e.g., Mother, Father, Guardian"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cell Phone *</label>
                <input
                  type="tel"
                  name="primaryContactCellPhone"
                  value={formData.primaryContactCellPhone}
                  onChange={handleChange}
                  required
                  placeholder="082 555 1234"
                />
              </div>
              
              <div className="form-group">
                <label>Work Phone</label>
                <input
                  type="tel"
                  name="primaryContactWorkPhone"
                  value={formData.primaryContactWorkPhone}
                  onChange={handleChange}
                  placeholder="011 555 1234"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Home Phone</label>
                <input
                  type="tel"
                  name="primaryContactHomePhone"
                  value={formData.primaryContactHomePhone}
                  onChange={handleChange}
                  placeholder="011 555 1234"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="primaryContactEmail"
                  value={formData.primaryContactEmail}
                  onChange={handleChange}
                  required
                  placeholder="sarah@example.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Secondary Contact (Optional)</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="secondaryContactName"
                  value={formData.secondaryContactName}
                  onChange={handleChange}
                  placeholder="e.g., John Smith"
                />
              </div>
              
              <div className="form-group">
                <label>Relationship</label>
                <input
                  type="text"
                  name="secondaryContactRelationship"
                  value={formData.secondaryContactRelationship}
                  onChange={handleChange}
                  placeholder="e.g., Father"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cell Phone</label>
                <input
                  type="tel"
                  name="secondaryContactCellPhone"
                  value={formData.secondaryContactCellPhone}
                  onChange={handleChange}
                  placeholder="082 555 1234"
                />
              </div>
              
              <div className="form-group">
                <label>Work Phone</label>
                <input
                  type="tel"
                  name="secondaryContactWorkPhone"
                  value={formData.secondaryContactWorkPhone}
                  onChange={handleChange}
                  placeholder="011 555 1234"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Home Phone</label>
                <input
                  type="tel"
                  name="secondaryContactHomePhone"
                  value={formData.secondaryContactHomePhone}
                  onChange={handleChange}
                  placeholder="011 555 1234"
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="secondaryContactEmail"
                  value={formData.secondaryContactEmail}
                  onChange={handleChange}
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {selectedSchool && (
            <div className="form-section">
              <h2>Fee Structure</h2>
              
              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <option value="term">Pay per Term (R{selectedSchool.schoolFee.termFee})</option>
                  <option value="instalment">Pay per Instalment (R{selectedSchool.schoolFee.instalmentFee})</option>
                </select>
              </div>

              <div className="form-group">
                <label>Annual Admin Fee (R) *</label>
                <input
                  type="number"
                  name="adminFee"
                  value={formData.adminFee}
                  onChange={handleChange}
                  required
                  placeholder="160"
                />
                <small>Can be adjusted or waived per student. Charged with Term 1.</small>
              </div>
               
              {/* NEW: Admin Fee Breakdown Display */}
              {adminFeeBreakdown && parseFloat(formData.adminFee) > 0 && (
                <div style={{
                  background: '#f0f4ff',
                  border: '1px solid #4169E1',
                  borderRadius: '8px',
                  padding: '15px',
                  marginTop: '15px'
                }}>
                  <strong style={{ color: '#4169E1', display: 'block', marginBottom: '10px' }}>
                    Admin Fee Breakdown (R{adminFeeBreakdown.total}):
                  </strong>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div>
                      <strong>Brand Building:</strong>
                      <div style={{ color: '#2e7d32', fontSize: '16px', fontWeight: 'bold' }}>
                        R{adminFeeBreakdown.bbContribution}
                      </div>
                    </div>
                    <div>
                      <strong>Franchisee Keeps:</strong>
                      <div style={{ color: '#1976d2', fontSize: '16px', fontWeight: 'bold' }}>
                        R{adminFeeBreakdown.franchiseeKeeps}
                      </div>
                    </div>
                    <div>
                      <strong>Head Office:</strong>
                      <div style={{ color: '#ed6c02', fontSize: '16px', fontWeight: 'bold' }}>
                        R{adminFeeBreakdown.headOfficeGets}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', gap: '12px' }}>
  <button 
    type="button" 
    onClick={onCancel} 
    className="btn-cancel"
    disabled={loading}
  >
    Cancel
  </button>
  <button 
    type="button"
    onClick={(e) => handleSubmit(e, false)}
    className="btn-submit" 
    disabled={loading}
    style={{ flex: 1 }}
  >
    {loading ? 'Adding...' : 'Add Student & Close'}
  </button>
  <button 
    type="button"
    onClick={(e) => handleSubmit(e, true)}
    className="btn-submit" 
    disabled={loading}
    style={{ flex: 1, background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' }}
  >
    {loading ? 'Adding...' : ' Add & Continue'}
  </button>
</div>
        </form>
      </div>
    </div>
  );
}

export default AddStudent;